import React from "react";
import { Calculator } from "lucide-react";
import { OriginBadge } from "../../lib/origin.jsx";
import { cn } from "../../lib/utils";

// Worked example laid out like a small calculation sheet.
export default function WorkedExample({ ex }) {
  return (
    <section id="example" data-testid="kc-example" className="scroll-mt-28 tile overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-b border-border bg-surface-2/50">
        <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-muted">
          <Calculator size={13} className="text-primary" /> Worked example
        </span>
        <OriginBadge origin="synthetic" source="Illustrative sample figures — not 1Rotary actuals" testid="kc-example-origin" />
      </div>
      <div className="px-5 py-4">
        <h3 className="font-display text-lg font-semibold tracking-tight" data-testid="kc-example-title">{ex.title}</h3>
        <p className="text-[13.5px] text-muted mt-1 leading-relaxed">{ex.intro}</p>
        {ex.table && <SheetTable t={ex.table} />}
        <ol className="mt-4 border border-border rounded-sm divide-y divide-border bg-bg/60" data-testid="kc-example-steps">
          {ex.steps.map((s, i) => (
            <li key={i} className="grid grid-cols-[1.6rem_minmax(0,1fr)] sm:grid-cols-[1.6rem_minmax(0,0.9fr)_minmax(0,1.2fr)_auto] gap-x-3 gap-y-0.5 px-3 py-2.5 items-baseline">
              <span className="font-mono-tabular text-[10.5px] text-subtle">{String(i + 1).padStart(2, "0")}</span>
              <span className="text-[13px] font-medium">{s.label}</span>
              <span className="col-start-2 sm:col-start-auto font-mono-tabular text-[12px] text-muted">{s.work}</span>
              <span data-testid={`kc-example-value-${i}`} className="col-start-2 sm:col-start-auto font-mono-tabular text-[13px] font-semibold text-primary sm:text-right whitespace-nowrap">{s.value}</span>
            </li>
          ))}
        </ol>
        <div data-testid="kc-example-result" className="mt-4 border-l-2 border-primary bg-primary/[0.05] px-4 py-3 text-[13.5px] leading-relaxed">{ex.result}</div>
      </div>
    </section>
  );
}

const isNum = (v) => typeof v === "number" || /^[₱+\-−]?[\d.,]+%?$|^₱/.test(String(v));

function SheetTable({ t }) {
  const num = t.columns.map((_, ci) => t.rows.every(r => isNum(r[ci])));
  return (
    <div className="mt-4 overflow-x-auto border border-border rounded-sm" data-testid="kc-example-table">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="bg-surface-2/60 border-b border-border">
            {t.columns.map((c, i) => <th key={i} className={cn("px-2.5 py-1.5 font-semibold text-[10.5px] uppercase tracking-wider text-muted whitespace-nowrap", num[i] ? "text-right" : "text-left")}>{c}</th>)}
          </tr>
        </thead>
        <tbody className="font-mono-tabular">
          {t.rows.map((r, ri) => (
            <tr key={ri} className={cn("border-b border-border/60 last:border-0", t.highlight.includes(ri) && "bg-primary/[0.06]")}>
              {r.map((v, ci) => <td key={ci} className={cn("px-2.5 py-1.5 whitespace-nowrap", num[ci] ? "text-right" : "text-left font-sans font-medium")}>{v}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
